/**
 * Shared Device Person Cache
 *
 * IndexedDB storage for offline Field lists per device and person:
 * - One database per device: `shared-device-cache-${deviceId}`
 * - One encrypted record per person in `cache` store
 * - Encryption: AES-GCM (256-bit) with fresh 12-byte IV per write
 * - Key derivation: PBKDF2 (200k iterations, SHA-256) over PIN
 *   Salt = deviceSalt + uid + ':person-cache'
 * - Non-extractable CryptoKey held in memory during session only, dropped at switch-out
 * - Plaintext cap: 2 MB (drops oldest/archived TODAY items first, flags trimmed: true)
 * - Expiry: 12 hours from switch-in; expired records deleted on read and startup
 */

import { getOrCreateDeviceSalt } from './sharedDeviceOfflineStore'
import {
  SHARED_DEVICE_OFFLINE_MAX_WINDOW_MS,
  type OpsDue,
  type OpsRequest,
  type OpsRequestKind,
  type OpsTemplate,
  type OpsTemplateVersion,
  type OpsAttendance,
  type OpsRoster,
  type OpsPhoneItemKey,
} from '../types/ops'
import type { HelperAssignedItem } from './helper/helperAssignedItemsService'
import type { StockItem, StockLocation } from '../types/stock'
import { fetchWorkspaceStock } from './stockService'
import { fetchStockLocations } from './stock/stockLedgerService'

const DB_VERSION = 1
const STORE_CACHE = 'cache'
const MAX_PLAINTEXT_BYTES = 2 * 1024 * 1024 // 2 MB
const PBKDF2_ITERATIONS = 200000

export interface SharedDeviceClockState {
  running: boolean
  id?: string
  workspaceId?: string
  uid?: string
  kind?: 'CLOCK' | 'TIMER'
  nodeId?: string | null
  startedAt?: string
  geoIn?: unknown
  device?: string | null
  occurredAtSource?: 'CLIENT' | 'SERVER'
}

export interface OpsCachedTemplate {
  template: OpsTemplate
  version: OpsTemplateVersion
}

export interface SharedDevicePersonSnapshot {
  todayItems: HelperAssignedItem[]
  stockItems: StockItem[]
  stockLocations: StockLocation[]
  clockState: SharedDeviceClockState | null
  trimmed?: boolean
  opsPhoneItems?: OpsPhoneItemKey[]
  opsDues?: OpsDue[]
  opsRequests?: OpsRequest[]
  opsRequestKinds?: OpsRequestKind[]
  opsTemplates?: OpsCachedTemplate[]
  opsAttendance?: OpsAttendance[]
  opsRoster?: OpsRoster | null
}

export interface StoredPersonCacheRecord {
  uid: string
  iv: string
  ciphertext: string
  expiresAt: number
  savedAt: number
  onlineSwitchInTime: number
}

// Map of open database promises per device
const dbCache = new Map<string, Promise<IDBDatabase>>()

const openCacheDb = (deviceId: string): Promise<IDBDatabase> => {
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB unavailable'))
  }
  const cached = dbCache.get(deviceId)
  if (cached) return cached

  const dbName = `shared-device-cache-${deviceId}`
  const dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(dbName, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_CACHE)) {
        db.createObjectStore(STORE_CACHE, { keyPath: 'uid' })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('IDB open failed'))
  })

  dbCache.set(deviceId, dbPromise)
  return dbPromise
}

const runCacheTx = async <T>(
  deviceId: string,
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T> | Promise<T>,
): Promise<T> => {
  const db = await openCacheDb(deviceId)
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE_CACHE, mode)
    const store = tx.objectStore(STORE_CACHE)

    let resultPromise: Promise<T> | IDBRequest<T>
    try {
      resultPromise = work(store)
    } catch (err) {
      reject(err)
      return
    }

    if (resultPromise instanceof Promise) {
      resultPromise.then(
        (res) => {
          tx.oncomplete = () => resolve(res)
        },
        (err) => reject(err),
      )
    } else {
      resultPromise.onsuccess = () => resolve(resultPromise.result)
      resultPromise.onerror = () => reject(resultPromise.error ?? new Error('Transaction request failed'))
    }

    tx.onerror = () => reject(tx.error ?? new Error('Transaction failed'))
  })
}

// ---------------------------------------------------------------------------
// Crypto helpers (AES-GCM + PBKDF2)
// ---------------------------------------------------------------------------

function bufferToBase64(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

function base64ToBuffer(b64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(b64)
  const buf = new ArrayBuffer(binary.length)
  const bytes = new Uint8Array(buf)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

export async function derivePersonCacheKey(
  deviceId: string,
  uid: string,
  pin: string,
): Promise<CryptoKey> {
  const deviceSalt = await getOrCreateDeviceSalt(deviceId)
  const saltLabel = new TextEncoder().encode(`${uid}:person-cache`)
  const combinedSalt = new Uint8Array(deviceSalt.length + saltLabel.length)
  combinedSalt.set(deviceSalt, 0)
  combinedSalt.set(saltLabel, deviceSalt.length)

  const pinBytes = new TextEncoder().encode(pin)
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    pinBytes,
    { name: 'PBKDF2' },
    false,
    ['deriveKey'],
  )

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: combinedSalt,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false, // non-extractable!
    ['encrypt', 'decrypt'],
  )
}

export async function encryptPersonSnapshot(
  key: CryptoKey,
  snapshot: SharedDevicePersonSnapshot,
): Promise<{ iv: string; ciphertext: string }> {
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const plaintext = JSON.stringify(snapshot)
  const encoded = new TextEncoder().encode(plaintext)

  const ciphertextBuf = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoded,
  )

  return {
    iv: bufferToBase64(iv),
    ciphertext: bufferToBase64(ciphertextBuf),
  }
}

export async function decryptPersonSnapshot(
  key: CryptoKey,
  ivB64: string,
  ciphertextB64: string,
): Promise<SharedDevicePersonSnapshot> {
  const iv = base64ToBuffer(ivB64)
  const ciphertext = base64ToBuffer(ciphertextB64)

  const decryptedBuf = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    ciphertext,
  )

  const plaintext = new TextDecoder().decode(decryptedBuf)
  return JSON.parse(plaintext) as SharedDevicePersonSnapshot
}

// ---------------------------------------------------------------------------
// 2 MB Plaintext Trimming
// ---------------------------------------------------------------------------

export function trimPersonSnapshot(
  snapshot: SharedDevicePersonSnapshot,
  maxBytes: number = MAX_PLAINTEXT_BYTES,
): SharedDevicePersonSnapshot {
  const json = JSON.stringify(snapshot)
  const byteLength = new TextEncoder().encode(json).length
  if (byteLength <= maxBytes) {
    return snapshot
  }

  // Need to trim: drop oldest TODAY items first, then archived ones
  const items = [...snapshot.todayItems]

  // Partition into archived / completed and active
  const isArchivedOrCompleted = (it: HelperAssignedItem) =>
    Boolean(it.isCompleted || (it as { status?: string }).status === 'archived')

  // Sort archived ascending by createdAt (oldest first)
  const archived = items
    .filter(isArchivedOrCompleted)
    .sort((a, b) => (Number(a.createdAt) || 0) - (Number(b.createdAt) || 0))

  // Sort active ascending by createdAt (oldest first)
  const active = items
    .filter((it) => !isArchivedOrCompleted(it))
    .sort((a, b) => (Number(a.createdAt) || 0) - (Number(b.createdAt) || 0))

  const keptItems = new Set<HelperAssignedItem>(items)

  // 1. Drop archived items starting from oldest
  let current: SharedDevicePersonSnapshot = {
    ...snapshot,
    todayItems: Array.from(keptItems),
    trimmed: true,
  }

  for (const it of archived) {
    keptItems.delete(it)
    current = {
      ...current,
      todayItems: Array.from(keptItems),
      trimmed: true,
    }
    if (new TextEncoder().encode(JSON.stringify(current)).length <= maxBytes) {
      return current
    }
  }

  // 2. Drop active items starting from oldest
  for (const it of active) {
    keptItems.delete(it)
    current = {
      ...current,
      todayItems: Array.from(keptItems),
      trimmed: true,
    }
    if (new TextEncoder().encode(JSON.stringify(current)).length <= maxBytes) {
      return current
    }
  }

  // 3. Ops attendance: marked entries first (PRESENT/ABSENT/LATE), oldest first; then remaining attendance
  if (current.opsAttendance && current.opsAttendance.length > 0) {
    const attList = [...current.opsAttendance]
    const isMarked = (a: OpsAttendance) =>
      ['PRESENT', 'ABSENT', 'LATE', 'HALF_DAY', 'ON_LEAVE'].includes(a.status)
    const getAttTime = (a: OpsAttendance): number => {
      const parseVal = (v: unknown): number => {
        if (typeof v === 'number') return v
        if (v && typeof v === 'object' && 'toMillis' in v && typeof (v as { toMillis: () => number }).toMillis === 'function') {
          return (v as { toMillis: () => number }).toMillis()
        }
        return 0
      }
      return parseVal(a.markedAt) || parseVal(a.updatedAt) || parseVal(a.createdAt)
    }
    const markedAtt = attList
      .filter(isMarked)
      .sort((a, b) => getAttTime(a) - getAttTime(b))
    const unmarkedAtt = attList.filter((a) => !isMarked(a))

    const keptAtt = new Set<OpsAttendance>(attList)
    for (const a of markedAtt) {
      keptAtt.delete(a)
      current = {
        ...current,
        opsAttendance: Array.from(keptAtt),
        trimmed: true,
      }
      if (new TextEncoder().encode(JSON.stringify(current)).length <= maxBytes) {
        return current
      }
    }
    for (const a of unmarkedAtt) {
      keptAtt.delete(a)
      current = {
        ...current,
        opsAttendance: Array.from(keptAtt),
        trimmed: true,
      }
      if (new TextEncoder().encode(JSON.stringify(current)).length <= maxBytes) {
        return current
      }
    }
  }

  // 4. Ops requests: closed / resolved / cancelled first, oldest first; then open requests oldest first
  if (current.opsRequests && current.opsRequests.length > 0) {
    const reqList = [...current.opsRequests]
    const isClosedReq = (r: OpsRequest) =>
      ['DONE', 'VERIFIED', 'CANCELLED', 'REJECTED'].includes(r.state)
    const closedReq = reqList
      .filter(isClosedReq)
      .sort((a, b) => (Number(a.updatedAt || a.createdAt) || 0) - (Number(b.updatedAt || b.createdAt) || 0))
    const openReq = reqList
      .filter((r) => !isClosedReq(r))
      .sort((a, b) => (Number(a.updatedAt || a.createdAt) || 0) - (Number(b.updatedAt || b.createdAt) || 0))

    const keptReq = new Set<OpsRequest>(reqList)
    for (const r of closedReq) {
      keptReq.delete(r)
      current = {
        ...current,
        opsRequests: Array.from(keptReq),
        trimmed: true,
      }
      if (new TextEncoder().encode(JSON.stringify(current)).length <= maxBytes) {
        return current
      }
    }
    for (const r of openReq) {
      keptReq.delete(r)
      current = {
        ...current,
        opsRequests: Array.from(keptReq),
        trimmed: true,
      }
      if (new TextEncoder().encode(JSON.stringify(current)).length <= maxBytes) {
        return current
      }
    }
  }

  // 5. Ops dues: completed / done / skipped dues oldest first, then open dues oldest first
  if (current.opsDues && current.opsDues.length > 0) {
    const dueList = [...current.opsDues]
    const isDoneDue = (d: OpsDue) => ['DONE', 'LATE_DONE', 'SKIPPED'].includes(d.status)
    const doneDues = dueList
      .filter(isDoneDue)
      .sort((a, b) => (Number(a.doneAt || a.skippedAt || a.windowStart) || 0) - (Number(b.doneAt || b.skippedAt || b.windowStart) || 0))
    const openDues = dueList
      .filter((d) => !isDoneDue(d))
      .sort((a, b) => (Number(a.windowStart) || 0) - (Number(b.windowStart) || 0))

    const keptDues = new Set<OpsDue>(dueList)
    for (const d of doneDues) {
      keptDues.delete(d)
      current = {
        ...current,
        opsDues: Array.from(keptDues),
        trimmed: true,
      }
      if (new TextEncoder().encode(JSON.stringify(current)).length <= maxBytes) {
        return current
      }
    }
    for (const d of openDues) {
      keptDues.delete(d)
      current = {
        ...current,
        opsDues: Array.from(keptDues),
        trimmed: true,
      }
      if (new TextEncoder().encode(JSON.stringify(current)).length <= maxBytes) {
        return current
      }
    }
  }

  // 6. Ops templates: drop templates not linked to remaining dues, then remaining templates
  if (current.opsTemplates && current.opsTemplates.length > 0) {
    const remainingDueTemplateIds = new Set<string>(
      (current.opsDues || [])
        .map((d) => d.templateId)
        .filter((id): id is string => typeof id === 'string' && id.length > 0),
    )
    const keptTemplates = current.opsTemplates.filter(
      (t) => Boolean(t.template.id) && remainingDueTemplateIds.has(t.template.id!),
    )
    current = {
      ...current,
      opsTemplates: keptTemplates,
      trimmed: true,
    }
    if (new TextEncoder().encode(JSON.stringify(current)).length <= maxBytes) {
      return current
    }
    current = {
      ...current,
      opsTemplates: [],
      trimmed: true,
    }
    if (new TextEncoder().encode(JSON.stringify(current)).length <= maxBytes) {
      return current
    }
  }

  // 7. Ops request kinds
  if (current.opsRequestKinds && current.opsRequestKinds.length > 0) {
    current = {
      ...current,
      opsRequestKinds: [],
      trimmed: true,
    }
  }

  return current
}

// ---------------------------------------------------------------------------
// Cache Persistence
// ---------------------------------------------------------------------------

export async function savePersonSnapshot(
  deviceId: string,
  uid: string,
  key: CryptoKey,
  snapshot: SharedDevicePersonSnapshot,
  switchInTime?: number,
  fixedExpiresAt?: number,
): Promise<void> {
  const trimmed = trimPersonSnapshot(snapshot)
  const { iv, ciphertext } = await encryptPersonSnapshot(key, trimmed)

  let expiresAt = fixedExpiresAt
  let onlineSwitchInTime = switchInTime

  if (expiresAt === undefined) {
    const existing = await getStoredPersonCacheRecordRaw(deviceId, uid)
    if (existing && existing.expiresAt) {
      expiresAt = existing.expiresAt
      onlineSwitchInTime = existing.onlineSwitchInTime ?? switchInTime ?? (existing.expiresAt - SHARED_DEVICE_OFFLINE_MAX_WINDOW_MS)
    } else {
      const baseTime = switchInTime ?? Date.now()
      onlineSwitchInTime = baseTime
      expiresAt = baseTime + SHARED_DEVICE_OFFLINE_MAX_WINDOW_MS
    }
  } else if (onlineSwitchInTime === undefined) {
    onlineSwitchInTime = expiresAt - SHARED_DEVICE_OFFLINE_MAX_WINDOW_MS
  }

  const savedAt = Date.now()

  const record: StoredPersonCacheRecord = {
    uid,
    iv,
    ciphertext,
    expiresAt,
    savedAt,
    onlineSwitchInTime,
  }

  await runCacheTx(deviceId, 'readwrite', (store) => {
    return store.put(record as unknown as Record<string, unknown>)
  })
}

export async function getPersonSnapshot(
  deviceId: string,
  uid: string,
  key: CryptoKey,
  now: number = Date.now(),
): Promise<SharedDevicePersonSnapshot | null> {
  const record = (await runCacheTx(deviceId, 'readonly', (store) => {
    return store.get(uid)
  })) as StoredPersonCacheRecord | undefined

  if (!record) return null

  // Check expiry (12 hours)
  if (now > record.expiresAt) {
    await runCacheTx(deviceId, 'readwrite', (store) => {
      return store.delete(uid)
    })
    return null
  }

  return decryptPersonSnapshot(key, record.iv, record.ciphertext)
}

export async function getStoredPersonCacheRecordRaw(
  deviceId: string,
  uid: string,
): Promise<StoredPersonCacheRecord | null> {
  const record = (await runCacheTx(deviceId, 'readonly', (store) => {
    return store.get(uid)
  })) as StoredPersonCacheRecord | undefined

  return record ?? null
}

export async function deleteExpiredPersonSnapshots(
  deviceId: string,
  now: number = Date.now(),
): Promise<void> {
  const all = (await runCacheTx(deviceId, 'readonly', (store) => {
    return store.getAll()
  })) as StoredPersonCacheRecord[]

  const expiredUids = all.filter((rec) => now > rec.expiresAt).map((r) => r.uid)
  if (expiredUids.length === 0) return

  await runCacheTx(deviceId, 'readwrite', (store) => {
    for (const u of expiredUids) {
      store.delete(u)
    }
    return store.get('dummy-complete')
  })
}

export async function deleteSharedDevicePersonCacheDb(deviceId: string): Promise<void> {
  const cached = dbCache.get(deviceId)
  if (cached) {
    try {
      const db = await cached
      db.close()
    } catch {
      // ignore
    }
    dbCache.delete(deviceId)
  }

  if (typeof indexedDB === 'undefined') return

  const dbName = `shared-device-cache-${deviceId}`
  await new Promise<void>((resolve, reject) => {
    const req = indexedDB.deleteDatabase(dbName)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error ?? new Error('Failed to delete cache database'))
    req.onblocked = () => resolve()
  })
}

// ---------------------------------------------------------------------------
// In-memory Session Person Cache Management
// ---------------------------------------------------------------------------

interface ActiveSessionCache {
  deviceId: string
  uid: string
  key: CryptoKey
  snapshot: SharedDevicePersonSnapshot
  onlineSwitchInTime: number
  expiresAt: number
}

let activeSessionCache: ActiveSessionCache | null = null
const cacheListeners = new Set<(snapshot: SharedDevicePersonSnapshot | null) => void>()

export function getActivePersonCache(): ActiveSessionCache | null {
  return activeSessionCache
}

export function subscribeActivePersonCache(
  listener: (snapshot: SharedDevicePersonSnapshot | null) => void,
): () => void {
  cacheListeners.add(listener)
  return () => {
    cacheListeners.delete(listener)
  }
}

function notifyCacheListeners(): void {
  const snap = activeSessionCache ? activeSessionCache.snapshot : null
  for (const listener of cacheListeners) {
    listener(snap)
  }
}

export async function openPersonCache(
  deviceId: string,
  uid: string,
  pin: string,
  now: number = Date.now(),
  isOnline: boolean = false,
  switchInTime?: number,
): Promise<SharedDevicePersonSnapshot | null> {
  const key = await derivePersonCacheKey(deviceId, uid, pin)
  const existing = await getPersonSnapshot(deviceId, uid, key, now)

  if (isOnline) {
    const onlineSwitchInTime = switchInTime ?? now
    const expiresAt = onlineSwitchInTime + SHARED_DEVICE_OFFLINE_MAX_WINDOW_MS
    const snapshot: SharedDevicePersonSnapshot = existing ?? {
      todayItems: [],
      stockItems: [],
      stockLocations: [],
      clockState: null,
    }
    activeSessionCache = {
      deviceId,
      uid,
      key,
      snapshot,
      onlineSwitchInTime,
      expiresAt,
    }
    notifyCacheListeners()
    // Persist to IDB immediately with the new online switch-in time and expiry
    await savePersonSnapshot(deviceId, uid, key, snapshot, onlineSwitchInTime, expiresAt)
    return existing
  }

  // Offline session: record must exist and not be expired
  if (!existing) {
    activeSessionCache = null
    notifyCacheListeners()
    return null
  }

  const raw = await getStoredPersonCacheRecordRaw(deviceId, uid)
  const onlineSwitchInTime =
    raw?.onlineSwitchInTime ??
    (raw?.expiresAt ? raw.expiresAt - SHARED_DEVICE_OFFLINE_MAX_WINDOW_MS : now)
  const expiresAt = raw?.expiresAt ?? now + SHARED_DEVICE_OFFLINE_MAX_WINDOW_MS

  activeSessionCache = {
    deviceId,
    uid,
    key,
    snapshot: existing,
    onlineSwitchInTime,
    expiresAt,
  }
  notifyCacheListeners()
  return existing
}

export function closePersonCache(): void {
  activeSessionCache = null
  notifyCacheListeners()
}

export async function updateActivePersonCache(
  updater: (prev: SharedDevicePersonSnapshot) => SharedDevicePersonSnapshot,
): Promise<void> {
  if (!activeSessionCache) return
  const nextSnapshot = updater(activeSessionCache.snapshot)
  activeSessionCache.snapshot = nextSnapshot
  notifyCacheListeners()

  try {
    await savePersonSnapshot(
      activeSessionCache.deviceId,
      activeSessionCache.uid,
      activeSessionCache.key,
      nextSnapshot,
      activeSessionCache.onlineSwitchInTime,
      activeSessionCache.expiresAt,
    )
  } catch (err) {
    console.warn('[sharedDevicePersonCache] Failed to persist updated cache:', err)
  }
}

export async function refreshSharedDeviceStockCache(
  workspaceId: string,
  stockFetcher: (wsId: string) => Promise<StockItem[]> = fetchWorkspaceStock,
  locationsFetcher: (wsId: string) => Promise<StockLocation[]> = fetchStockLocations,
): Promise<void> {
  if (!workspaceId) return
  const active = getActivePersonCache()
  if (!active) return

  const [rawItems, rawLocations] = await Promise.all([
    stockFetcher(workspaceId).catch((err) => {
      console.warn('[sharedDevicePersonCache] Failed to fetch stock items:', err)
      return [] as StockItem[]
    }),
    locationsFetcher(workspaceId).catch((err) => {
      console.warn('[sharedDevicePersonCache] Failed to fetch stock locations:', err)
      return [] as StockLocation[]
    }),
  ])

  const sorted = [...rawItems].sort((a, b) => {
    const timeA =
      (a.updatedAt as { toMillis?: () => number } | undefined)?.toMillis?.() ??
      (a.lastUpdated as { toMillis?: () => number } | undefined)?.toMillis?.() ??
      (a.createdAt as { toMillis?: () => number } | undefined)?.toMillis?.() ?? 0
    const timeB =
      (b.updatedAt as { toMillis?: () => number } | undefined)?.toMillis?.() ??
      (b.lastUpdated as { toMillis?: () => number } | undefined)?.toMillis?.() ??
      (b.createdAt as { toMillis?: () => number } | undefined)?.toMillis?.() ?? 0
    return timeB - timeA
  })

  const isTrimmed = sorted.length > 2000
  const itemsToKeep = isTrimmed ? sorted.slice(0, 2000) : sorted

  const sanitizedStockItems: StockItem[] = itemsToKeep.map((item) => {
    const rawLocId =
      (item as unknown as { locationId?: string | null }).locationId ?? item.location ?? null
    return {
      id: item.id,
      workspaceId: item.workspaceId,
      name: item.name,
      sku: item.sku,
      barcode: item.barcode ?? null,
      unit: item.unit,
      quantity: item.quantity ?? 0,
      reservedQuantity: item.reservedQuantity ?? 0,
      location: item.location ?? '',
      locationId: rawLocId,
      status: item.status ?? 'Available',
      lastUpdated: item.lastUpdated ?? null,
      linkedTaskIds: item.linkedTaskIds ?? [],
    } as unknown as StockItem
  })

  await updateActivePersonCache((prev) => ({
    ...prev,
    stockItems: sanitizedStockItems,
    stockLocations: rawLocations,
    trimmed: isTrimmed || prev.trimmed,
  }))
}

